<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card :style="{ minWidth: '600px' }" class="relative-position">
      <q-card-section
        class="q-px-2x-md q-py-xl flex justify-between items-center"
      >
        <span class="text-lg text-neutral text-weight-medium">
          {{ title }}
        </span>
        <PBtn color="primary" flat dense round @click="onCancel">
          <CloseIcon class="icon" />
        </PBtn>
      </q-card-section>
      <q-separator class="fullWidth" />
      <q-card-section class="q-px-2x-md q-pt-xl q-pb-2x-md">
        <q-form greedy @submit.prevent="onSubmit">
          <!-- Only for agents -->
          <PField
            v-if="props.isAgent"
            v-model="selectedRole"
            label="Choose Party Type"
            required
          >
            <template #control>
              <POptionGroup
                v-model="selectedRole"
                :options="roleOptions"
                @update:model-value="updateRole"
              />
            </template>
          </PField>

          <PSelectInput
            v-model="selectedUser"
            option-label="shop_name"
            option-value="_id"
            :label="userSelectorLabel"
            :placeholder="userSelectorLabel"
            :options="unref(linkedUserFilter.entities)"
            :options-loading="unref(linkedUserFilter.loading)"
            use-chips
            multiple
            clearable
            required
            :max-values="computedMaxValues"
            :validations="{ required: true }"
            :style="{ width: '535px' }"
            @filter="linkedUserFilter.onFilter"
            @filter-abort="linkedUserFilter.onFilterAbort"
            @virtual-scroll="linkedUserFilter.onScroll"
            @update:model-value="onUpdateDropDown"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label class="row items-center">
                    <q-avatar
                      color="info-20"
                      font-size="14px"
                      size="32px"
                      text-color="info-70"
                    >
                      <span class="text-uppercase">
                        {{ getAvatarInitials(opt.shop_name) }}
                      </span>
                    </q-avatar>
                    <div class="column q-ml-sm text-sm">
                      <span class="text-neutral">
                        {{ opt.shop_name }}
                      </span>
                      <span
                        v-if="opt.gstin"
                        caption
                        lines="1"
                        class="text-neutral-70 text-xs"
                      >
                        {{ opt.gstin + ", " + opt.state }}
                      </span>
                    </div>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>

          <div class="flex justify-end">
            <PBtn
              :fluid="false"
              :loading="isLinking"
              label="Submit"
              type="submit"
            />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useQuasar, useDialogPluginComponent } from "quasar";
import CloseIcon from "assets/icons/close.svg";
import { getLinkedUsers } from "src/api/user.js";
import { getAvatarInitials } from "src/assets/scripts/utilities/helpers";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

const $q = useQuasar();

const props = defineProps({
  isAgent: {
    type: Boolean,
    default: false,
  },
  linkUserRole: {
    type: String,
    default: "",
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

const selectedUser = ref(null);
const selectedRole = ref("buyer");
const isLinking = ref(false);
const linkedUserFilter = ref();
const selectedUserNames = ref([]);

const { user } = storeToRefs(useUserStore());
const { putLinkAssociatedUsers } = useUserStore();

const onCancel = async () => {
  onDialogCancel();
};

const linkAssociatedUsers = async () => {
  try {
    isLinking.value = true;

    await putLinkAssociatedUsers({
      link_user: selectedUser.value,
      isAgent: props.isAgent,
    });

    isLinking.value = false;
  } catch (error) {
    console.error(error);

    isLinking.value = false;
  }

  onDialogOK();
};

const onSubmit = async () => {
  if (!selectedUser.value?.length) return;

  if (props.isAgent) {
    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        text: `Are you sure you want to link ${selectedUserNames.value.join(", ")} to ${unref(user)?.name}?`,
        isFooterVisible: true,
        submittable: true,
        cancellable: true,
        submitLabel: "Yes",
        cancelLabel: "Cancel",
        closable: false,
        onConfirm: async () => {
          await linkAssociatedUsers();
        },
      },
    });
  } else {
    await linkAssociatedUsers();
  }
};

const onUpdateDropDown = (user_id) => {
  if (user_id) {
    selectedUserNames.value = linkedUserFilter.value.entities
      .filter((user) => user_id.includes(user._id))
      .map((user) => user.shop_name);
  }
};

const title = computed(() => {
  return props.isAgent
    ? "Associate"
    : "Link " + useChangeCase(props.linkUserRole || "", "capitalCase").value;
});

const roleOptions = computed(() => [
  { label: "Customer", value: "buyer" },
  { label: "Vendor", value: "seller" },
]);

const userSelectorLabel = computed(() => {
  if (props.isAgent) {
    return selectedRole.value === "buyer" ? "Select Customer" : "Select Vendor";
  }

  return `Select ${useChangeCase(props.linkUserRole || "", "capitalCase").value}`;
});

const linkedUserRoleValue = computed(() => {
  if (props.isAgent) {
    return selectedRole.value;
  }

  return props.linkUserRole === "customer" ? "buyer" : "seller";
});

const computedMaxValues = computed(() => {
  if (props.isAgent) {
    return 3;
  }

  return undefined;
});

const initLinkedUserFilter = () => {
  linkedUserFilter.value = useFilter({
    getter: getLinkedUsers,
    options: {
      params: {
        user_id: unref(user)?._id,
        role: `${props.isAgent ? selectedRole.value : linkedUserRoleValue.value}`,
        isAgent: props.isAgent,
      },
    },
  });
};

onMounted(() => {
  initLinkedUserFilter();
});

const updateRole = () => {
  selectedUser.value = null;

  if (props.isAgent) {
    initLinkedUserFilter();
  }
};
</script>
